// widget-chart.service.ts

import { Chart, ChartData, ChartOptions } from 'chart.js';

// Определение цветов для чарта
const colors = [
  'rgba(151,187,205,1)',
  'rgba(220,220,220,1)',
  'rgba(247,70,74,1)',
  'rgba(70,191,189,1)',
  'rgba(253,180,92,1)',
  'rgba(148,159,177,1)',
  'rgba(77,83,96,1)',
];

const borderColors = [
  'rgba(151,187,205,1)',
  'rgba(220,220,220,1)',
  'rgba(247,70,74,1)',
  'rgba(70,191,189,1)',
  'rgba(253,180,92,1)',
  'rgba(148,159,177,1)',
  'rgba(77,83,96,1)',
];

// Функция для получения цвета
export const getColor = (context: { dataIndex?: number } | null, index: number | null = null): string => {
  if (index === null && context && context.dataIndex !== undefined) {
    index = context.dataIndex;
  }

  if (index !== null && colors[index]) {
    return colors[index];
  } else {
    let i = colors.length - 1;
    let colorIndex = 0;
    let foundColor = false;
    while (!foundColor && index !== null) {
      i++;
      if (i === index) {
        foundColor = true;
      }
      if (!foundColor) {
        colorIndex++;
        if (colorIndex > colors.length - 1) {
          colorIndex = 0;
        }
      }
    }
    return colors[colorIndex];
  }
};

// Функция для получения граничного цвета
export const getBorderColor = (context: { dataIndex?: number } | null, index: number | null = null): string => {
  if (index === null && context && context.dataIndex !== undefined) {
    index = context.dataIndex;
  }

  if (index !== null && borderColors[index]) {
    return borderColors[index];
  } else {
    let i = borderColors.length - 1;
    let colorIndex = 0;
    let foundColor = false;
    while (!foundColor && index !== null) {
      i++;
      if (i === index) {
        foundColor = true;
      }
      if (!foundColor) {
        colorIndex++;
        if (colorIndex > borderColors.length - 1) {
          colorIndex = 0;
        }
      }
    }
    return borderColors[colorIndex];
  }
};

// Интерфейс для данных чарта
export interface ChartDataInput {
  labels?: (string | null | undefined)[];
  data?: (number | null | undefined)[];
}

// Функция для форматирования данных чарта
export const formatChartData = ({ labels = [], data = [] }: ChartDataInput): ChartData<'doughnut'> => ({
  labels: labels.filter((label): label is string => typeof label === 'string'),
  datasets: [
    {
      data: data.filter((value): value is number => typeof value === 'number'),
      backgroundColor: labels.map((_, index) => getColor(null, index)),
      borderColor: labels.map((_, index) => getBorderColor(null, index)),
      borderWidth: 1,
    },
  ],
});

// Функция для создания опций чарта
export const getChartOptions = (title: string, legendId: string): ChartOptions<'doughnut'> => ({
  //@ts-ignore
  legendId,
  responsive: true,
  plugins: {
    title: {
      display: false, // Убираем заголовок с диаграммы
    },
    legend: {
      display: false, // Отключаем встроенную легенду
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: (context) => {
          const data = context.dataset.data as number[];
          const totalSum = data.reduce((a, b) => a + b, 0);
          const percentage = Math.round((context.raw as number / totalSum) * 100);
          return `${context.label}: ${context.raw} (${percentage}%)`;
        },
      },
    },
  },
  elements: {
    arc: {
      borderWidth: 0, // Убираем границы между сегментами
    },
  },
});

// Функция для создания HTML легенды
export const generateLegend = (chart: Chart<'doughnut'>): string => {
  const labels = chart.data.labels || [];
  const dataset = chart.data.datasets[0]?.data || [];
  const numericData = dataset.map((value) => (typeof value === 'number' ? value : Number(value) || 0));
  const total = numericData.reduce((acc, value) => acc + value, 0);

  const text: string[] = [];
  text.push(
    '<div class="chart-legend" style="display:flex;flex-wrap:wrap;gap:8px;justify-content:center;width:100%;">'
  );

  for (let i = 0; i < numericData.length; i++) {
    const rawLabel = labels[i];
    const label = typeof rawLabel === 'string' ? rawLabel : `Item ${i + 1}`;
    const value = numericData[i];
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
    const chipColor = getColor(null, i);

    text.push(
      `<div class="chart-legend-item" style="display:flex;align-items:center;gap:8px;background:#f1f5f9;padding:6px 12px;border-radius:9999px;font-size:12px;font-weight:600;color:#0f172a;">`
    );
    text.push(
      `<span style="display:inline-block;width:10px;height:10px;border-radius:9999px;background-color:${chipColor};"></span>`
    );
    text.push(`<span style="white-space:nowrap;">${label}</span>`);
    text.push(
      `<span style="color:#64748b;font-weight:500;">${value}${total > 0 ? ` (${percentage}%)` : ''}</span>`
    );
    text.push('</div>');
  }

  text.push('</div>');
  return text.join('');
};
